import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Textarea,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Box,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { AlertCircle } from 'lucide-react';
import { useEffect, useRef } from 'react';

interface ResolveIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (resolutionNotes: string) => Promise<void>;
  issueTitle: string;
  isLastIssue?: boolean;
  onMarkOnTrack?: (resolutionNotes: string) => Promise<void>;
}

/**
 * Modal to capture resolution notes when resolving an issue
 * Also handles the case where it's the last issue for a rock
 */
export function ResolveIssueModal({
  isOpen,
  onClose,
  onConfirm,
  issueTitle,
  isLastIssue = false,
  onMarkOnTrack,
}: ResolveIssueModalProps) {
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const validationSchema = Yup.object({
    notes: Yup.string()
      .required('Resolution notes are required')
      .min(5, 'Please provide more detail about how this was resolved'),
  });

  const warningBg = useColorModeValue('yellow.50', 'yellow.900');
  const warningBorder = useColorModeValue('yellow.200', 'yellow.700');
  const warningIconColor = useColorModeValue('orange.600', 'orange.300');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Resolve Issue</ModalHeader>
        <ModalCloseButton />
        
        <Formik
          initialValues={{ notes: '' }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              if (isLastIssue && onMarkOnTrack) {
                await onMarkOnTrack(values.notes);
              } else {
                await onConfirm(values.notes);
              }
              
              if (isMountedRef.current) {
                onClose();
              }
            } catch (error) {
              console.error('Error in ResolveIssueModal submit:', error);
            } finally {
              if (isMountedRef.current) {
                setSubmitting(false);
              }
            }
          }}
        >
          {({ isSubmitting, isValid, dirty, submitForm }) => (
            <Form>
              <ModalBody>
                <VStack align="stretch" spacing={4}>
                  {isLastIssue && (
                    <Box
                      bg={warningBg}
                      borderWidth={1}
                      borderColor={warningBorder}
                      borderRadius="md"
                      p={4}
                    >
                      <Box display="flex" alignItems="start" gap={3}>
                        <Icon
                          as={AlertCircle}
                          w={5}
                          h={5}
                          color={warningIconColor}
                          flexShrink={0}
                          mt={0.5}
                        />
                        <Box flex={1}>
                          <Text fontSize="sm" color="gray.700">
                            Resolving this last issue will automatically move the rock to On Track status.
                          </Text>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  <Text>
                    How was the issue <strong>"{issueTitle}"</strong> resolved?
                  </Text>
                  
                  <Field name="notes">
                    {({ field, form }: any) => (
                      <FormControl isInvalid={form.errors.notes && form.touched.notes} isRequired>
                        <FormLabel>Resolution Notes</FormLabel>
                        <Textarea
                          {...field}
                          placeholder="Describe the actions taken to resolve this issue..."
                          rows={4}
                        />
                        <FormErrorMessage>{form.errors.notes}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>

              <ModalFooter>
                <VStack width="100%" spacing={3}>
                  {isLastIssue ? (
                    <>
                      <Button
                        colorScheme="green"
                        w="full"
                        isLoading={isSubmitting}
                        isDisabled={!isValid || !dirty}
                        onClick={submitForm}
                      >
                        Resolve & Move Rock to On Track
                      </Button>
                      <Button variant="ghost" w="full" onClick={onClose}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Box display="flex" justifyContent="flex-end" width="100%" gap={3}>
                      <Button variant="ghost" onClick={onClose}>
                        Cancel
                      </Button>
                      <Button
                        colorScheme="green"
                        type="submit"
                        isLoading={isSubmitting}
                        isDisabled={!isValid || !dirty}
                      >
                        Mark as Resolved
                      </Button>
                    </Box>
                  )}
                </VStack>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
}
